import { api } from ".";

/**
 * CLE-2323 / CLE-2331 — RTK Query layer for the rate-tools endpoints. Both endpoints
 * accept multipart uploads and return binary downloads (ZIP for the splitter, CSV for
 * the expander). We pull diagnostic headers off the response (split file mode, row
 * counts, list of unmapped service lines) and return them alongside the blob so the
 * SPA can show a results banner.
 */

export interface ISplitResponse {
  blob: Blob;
  headers: Record<string, string>;
}

export interface ISplitRequest {
  file: File;
  includeFacility: boolean;
  includeNonFacility: boolean;
  includeServiceLine: boolean;
  /**
   * CLE-2464 — service lines for which the non-facility output should prefer the technical
   * component (TC) rate over global. Sent as a JSON array; omitted when empty.
   */
  tcServiceLines?: string[];
}

export interface IExpandResponse {
  blob: Blob;
  headers: Record<string, string>;
}

export interface IExpandRequest {
  baseFile: File;
  rulesFile: File;
  accountId: string;
  payerId: number;
  /**
   * Claim type used to scope the per-payer service-line library lookup
   * (CLE-2323 follow-on). Defaults to PROFESSIONAL on the server when omitted.
   */
  claimType?: "PROFESSIONAL" | "OUTPATIENT" | "INPATIENT";
}

/** Surface 400-JSON errors from the expander (rules-sheet validation) with errors[] preserved. */
export interface IExpandValidationError {
  message: string;
  errors: string[];
}

function collectHeaders(response: Response): Record<string, string> {
  const out: Record<string, string> = {};
  response.headers.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

const rateToolsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    splitRateFile: builder.mutation<ISplitResponse, ISplitRequest>({
      query: ({
        file,
        includeFacility,
        includeNonFacility,
        includeServiceLine,
        tcServiceLines,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("includeFacility", String(includeFacility));
        formData.append("includeNonFacility", String(includeNonFacility));
        formData.append("includeServiceLine", String(includeServiceLine));
        if (tcServiceLines && tcServiceLines.length > 0) {
          formData.append("tcServiceLines", JSON.stringify(tcServiceLines));
        }
        return {
          url: "contract-fee-schedules/tools/split",
          method: "POST",
          body: formData,
          responseHandler: async (response: Response) => ({
            blob: await response.blob(),
            headers: collectHeaders(response),
          }),
        };
      },
    }),
    expandRateFile: builder.mutation<IExpandResponse, IExpandRequest>({
      query: ({ baseFile, rulesFile, accountId, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("baseFile", baseFile);
        formData.append("rulesFile", rulesFile);
        formData.append("accountId", accountId);
        formData.append("payerId", String(payerId));
        if (claimType) formData.append("claimType", claimType);
        return {
          url: "contract-fee-schedules/tools/expand",
          method: "POST",
          body: formData,
          responseHandler: async (response: Response) => ({
            blob: await response.blob(),
            headers: collectHeaders(response),
          }),
        };
      },
    }),
  }),
});

export const { useSplitRateFileMutation, useExpandRateFileMutation } =
  rateToolsQueries;
